/**
 * SetTableTablespaceFormBody — name + per-engine fields orchestrator for the
 * tablespace / filegroup creation flow surfaced from `StorageTab` and the
 * inline DDL toolbar (DSE2 §3.E2).
 *
 * Per-engine surface:
 *   - postgres: name + LOCATION + owner (optional)
 *   - oracle:   name + DATAFILE + size selector (BIGFILE/SMALLFILE)
 *   - mssql:    filegroup name + database
 *   - mysql / sqlite: form not rendered — caller surfaces the disabled
 *     affordance via `getSetTableTablespaceUnsupportedTooltip`.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice E2.
 */
import { useState } from 'react';

import { Button } from '../../../../../components/ui/button';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../components/ui/select';
import {
  commitSetTableTablespace,
  getSetTableTablespaceKind,
  validateSetTableTablespaceForEngine,
  type SetTableTablespaceArgs,
} from '../../edit/edits/setTableTablespace';
import type { VqeDdlEditContext } from '../../edit/editContext';
import { FormEngineChip } from './FormEngineChip';

/** Radix Select forbids `value=""`; the "(default)" oracle size class rides
 *  this sentinel through the widget and maps back to `''` on the way out. */
const UNSET = '__unset__' as const;

export interface SetTableTablespaceFormBodyProps {
  readonly editContext: VqeDdlEditContext;
  readonly testId: string;
  /** Default mssql database scope when the editor isn't bound to a connection. */
  readonly defaultDatabase?: string | null;
  readonly onCommitted?: () => void;
  readonly onCancel?: () => void;
}

export function SetTableTablespaceFormBody(props: SetTableTablespaceFormBodyProps) {
  const { editContext, testId, defaultDatabase, onCommitted, onCancel } = props;
  const engine = editContext.service.engineId;
  const kind = getSetTableTablespaceKind(engine);

  const [name, setName] = useState('');
  const [location, setLocation] = useState('');
  const [owner, setOwner] = useState('');
  const [oracleSize, setOracleSize] = useState<'' | 'bigfile' | 'smallfile'>('');
  const [database, setDatabase] = useState(defaultDatabase ?? editContext.database ?? '');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  function buildArgs(): SetTableTablespaceArgs | { error: string } {
    if (kind === 'tablespace') {
      const trimmedName = name.trim();
      const trimmedLocation = location.trim();
      const trimmedOwner = owner.trim();
      if (engine === 'oracle') {
        return {
          kind: 'tablespace',
          def: {
            name: trimmedName,
            location: trimmedLocation,
            ...(oracleSize !== '' ? { oracleSize } : {}),
          },
        };
      }
      // postgres
      return {
        kind: 'tablespace',
        def: {
          name: trimmedName,
          location: trimmedLocation,
          ...(trimmedOwner !== '' ? { owner: trimmedOwner } : {}),
        },
      };
    }
    if (kind === 'filegroup') {
      return {
        kind: 'filegroup',
        def: { name: name.trim(), database: database.trim() },
      };
    }
    return { error: `Tablespace creation is not supported on ${engine}.` };
  }

  async function handleSubmit() {
    setError(null);
    const args = buildArgs();
    if ('error' in args) { setError(args.error); return; }
    const validation = validateSetTableTablespaceForEngine(engine, args);
    if (validation !== null) { setError(validation); return; }
    setPending(true);
    try {
      await commitSetTableTablespace(editContext, args);
      onCommitted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPending(false);
    }
  }

  const isFilegroup = kind === 'filegroup';
  const isOracle = engine === 'oracle';
  const isPostgres = engine === 'postgres';

  return (
    <div data-testid={testId} className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold">
          {isFilegroup ? 'Add filegroup' : 'Create tablespace'}
        </div>
        <FormEngineChip engine={engine} />
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor={`${testId}-name`} className="text-xs">
          Name <span className="text-destructive">*</span>
        </Label>
        <Input
          id={`${testId}-name`}
          data-testid={`${testId}-name`}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={isFilegroup ? 'fg_warm' : 'ts_data'}
          className="h-8 font-mono text-xs"
        />
      </div>

      {isFilegroup ? (
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${testId}-database`} className="text-xs">
            Database <span className="text-destructive">*</span>
          </Label>
          <Input
            id={`${testId}-database`}
            data-testid={`${testId}-database`}
            value={database}
            onChange={(e) => setDatabase(e.target.value)}
            placeholder="AppDb"
            className="h-8 font-mono text-xs"
          />
          <span className="text-[10px] text-muted-foreground">
            mssql filegroups are scoped to a database via <code>ALTER DATABASE</code>.
          </span>
        </div>
      ) : (
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${testId}-location`} className="text-xs">
            {isOracle ? 'Datafile path' : 'Location'} <span className="text-destructive">*</span>
          </Label>
          <Input
            id={`${testId}-location`}
            data-testid={`${testId}-location`}
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder={
              isOracle
                ? '/u01/app/oracle/ts_app.dbf'
                : '/var/lib/postgresql/ts_data'
            }
            className="h-8 font-mono text-xs"
          />
          <span className="text-[10px] text-muted-foreground">
            {isOracle ? 'Emitted as DATAFILE ‘path’.' : 'Emitted as LOCATION ‘directory’.'}
          </span>
        </div>
      )}

      {isPostgres ? (
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${testId}-owner`} className="text-xs">
            Owner (optional)
          </Label>
          <Input
            id={`${testId}-owner`}
            data-testid={`${testId}-owner`}
            value={owner}
            onChange={(e) => setOwner(e.target.value)}
            placeholder="app"
            className="h-8 font-mono text-xs"
          />
        </div>
      ) : null}

      {isOracle ? (
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${testId}-size`} className="text-xs">
            Size class (optional)
          </Label>
          <Select
            value={oracleSize === '' ? UNSET : oracleSize}
            onValueChange={(v) =>
              setOracleSize(v === UNSET ? '' : (v as 'bigfile' | 'smallfile'))
            }
          >
            <SelectTrigger
              id={`${testId}-size`}
              data-testid={`${testId}-size`}
              className="h-8 font-mono text-xs"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={UNSET}>(default)</SelectItem>
              <SelectItem value="bigfile">BIGFILE</SelectItem>
              <SelectItem value="smallfile">SMALLFILE</SelectItem>
            </SelectContent>
          </Select>
        </div>
      ) : null}

      {error ? (
        <div
          className="rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1 text-xs text-destructive"
          data-testid={`${testId}-error`}
        >
          {error}
        </div>
      ) : null}

      <div className="sticky bottom-0 -mx-4 -mb-4 flex items-center justify-end gap-2 border-t bg-popover px-4 py-2">
        <Button type="button" variant="ghost" size="sm" onClick={() => onCancel?.()} data-testid={`${testId}-cancel`}>
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={pending}
          onClick={() => void handleSubmit()}
          data-testid={`${testId}-submit`}
        >
          {pending ? 'Applying…' : 'Apply'}
        </Button>
      </div>
    </div>
  );
}
